import * as React from "react";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
} from "@coreui/react";
import {
  debounce,
  Paper,
  StyledEngineProvider,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SearchElement } from "../../components/Search/Search";
import { TableBodyElement } from "../../components/TableBody/TableBodyElement";
import { TableHeadElement } from "../../components/TableBody/TableHeadElement";
import { ColumnsType, Order, PageMeta } from "../../types";
import ConfirmBox from "../../components/ConfirmBox/ConfirmBox";
import {
  useActiveInactivePortfolioMutation,
  useDeletePortfolioMutation,
  usePortfolioListQuery,
  useHomeStatusPortfolioMutation
} from "../../redux-services/api/PortfolioApi";

const tableColumns: ColumnsType[] = [
  {
    id: "id",
    sort: false,
    label: "#",
  },
  {
    id: "client_name",
    sort: true,
    label: "Project Name",
  },
  {
    id: "project_duration",
    sort: true,
    label: "Project Duration",
  },
  {
    id: "expertise",
    sort: true,
    label: "Expertise",
  },
  {
    id: "portfolio_status",
    sort: true,
    label: "Portfolio Status",
  },
  {
    id: "home_status_label",
    sort: true,
    label: "Home",
  },
  {
    id: "action",
    sort: false,
    label: "Action",
    style: { width: "150px" },
  },
];

export interface SortingData {
  id: string;
  name: string;
  email: string;
  phone: string;
  createdAt: string;
  updatedAt?: string;
}

let initialOrder: string = process.env.REACT_APP_ORDER as string;
const initialState: PageMeta = {
  page: 1,
  per_page: Number(process.env.REACT_APP_PER_PAGE),
  searchText: "",
  order: process.env.REACT_APP_ORDER,
  order_by: process.env.REACT_APP_ORDER_BY,
};

export const Portfolio: React.FunctionComponent = (props) => {
  let navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const [orderBy, setOrderBy] = useState<keyof SortingData>(
    process.env.REACT_APP_ORDER_BY as keyof SortingData
  );
  const [order, setOrder] = useState<Order>(initialOrder as Order);
  const [search, setSearch] = useState<string>("");
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    initialState.per_page as number
  );
  const [portfolioListState, setPortfolioListState] =
    useState<PageMeta>(initialState);
  const [isClose, setIsClose] = useState<boolean>(false);
  const [Id, setId] = useState<string>("");

  const { data } = usePortfolioListQuery({
    per_page: portfolioListState.per_page ?? rowsPerPage,
    page: portfolioListState.page ?? page,
    order: portfolioListState.order ?? order,
    order_by: portfolioListState.order_by ?? orderBy,
    searchText: portfolioListState.searchText ?? search,
  });

  const [activeInactivePortfolio, editStatus] =
    useActiveInactivePortfolioMutation();

  const [homeStatusPortfolio, editHomeStatus] =
    useHomeStatusPortfolioMutation()

  const [deletePortfolio] = useDeletePortfolioMutation();

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
    setPortfolioListState({ ...portfolioListState, page: newPage + 1 });
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPortfolioListState({
      ...portfolioListState,
      per_page: parseInt(event.target.value),
      page: 1,
    });
    setPage(1);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof SortingData
  ) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(property);
    setPortfolioListState({
      ...portfolioListState,
      order: newOrder,
      order_by: property,
    });
  };

  const createSortHandler = (
    event: React.MouseEvent<unknown>,
    property: keyof SortingData
  ) => {
    if (property !== ("action" as keyof SortingData)) {
      handleRequestSort(event, property);
    }
  };

  const searchHandler = useMemo(() => {
    return debounce((event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
      setPortfolioListState({
        page: 1,
        per_page: portfolioListState.per_page,
        order: initialOrder,
        searchText: event.target.value,
      });
    }, 800);
  }, []);
  const confirmBoxClose = (res: boolean) => {
    setIsClose(res);
    if (res) {
      setIsClose(false);
      deletePortfolio(Id);
    } else {
      setIsClose(res);
    }
  };
  const changeStauts = (id: string, status: boolean) => {
    activeInactivePortfolio({ id, status });
  };
  const changeHomeStauts = (id: string, home_status: boolean) => {
    homeStatusPortfolio({ id, home_status });
  };
  return (
    <CRow>
      <CCol xs={12}>
        <CCard className="mb-4">
          <CCardHeader
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <strong>Portfolio List</strong>
            <Tooltip title="Add Portfolio" arrow>
              <CButton
                size="sm"
                color="primary"
                variant="outline"
                onClick={() => navigate("/portfolio/add")}
              >
                Add Portfolio
              </CButton>
            </Tooltip>
          </CCardHeader>
          <CCardBody className="bg-transparent">
            <SearchElement
              searchFn={searchHandler}
              searchTag={"Search Portfolio..."}
            />
            <StyledEngineProvider>
              <TableContainer component={Paper}>
                <Table
                  sx={{ minWidth: 650 }}
                  aria-label="simple table bordered"
                  className="table-bordered table"
                >
                  <TableHead>
                    <TableRow>
                      <TableHeadElement
                        setHead={tableColumns ? tableColumns : []}
                        order={order}
                        orderBy={orderBy}
                        sortFn={(event, id: string) =>
                          createSortHandler(event, id as keyof SortingData)
                        }
                      />
                    </TableRow>
                  </TableHead>
                  <TableBody className="actionbox">
                    <TableBodyElement
                      selectedColumns={[
                        "client_name",
                        "project_duration",
                        "expertise",
                        "portfolio_status",
                        "home_status_label",
                      ]}
                      setData={data?.response.data ? data?.response.data : []}
                      editFn={(id) => navigate(`/portfolio/edit/${id}`)}
                      deleteFn={(id) => {
                        setIsClose(true);
                        setId(id);
                      }}
                      statusFn={(id: string, status: boolean) => {
                        changeStauts(id, status);
                      }}
                      statusHomeFn={(id: string, home_status: boolean) => {
                        changeHomeStauts(id, home_status);
                      }}
                      styleFn={"20%"}
                      pageData={{
                        limit:
                          rowsPerPage ?? Number(process.env.REACT_APP_PER_PAGE),
                        page: data?.response.page as number,
                      }}
                    />
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[20, 50, 100]}
                  count={data?.response.total ?? 0}
                  page={data?.response.page ?? 0}
                  onPageChange={handleChangePage}
                  rowsPerPage={
                    rowsPerPage ?? Number(process.env.REACT_APP_PER_PAGE)
                  }
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            </StyledEngineProvider>
          </CCardBody>
        </CCard>
      </CCol>
      <ConfirmBox
        title="Portfolio Confirmation"
        message="Are you sure, you want to delete this portfolio?"
        open={isClose}
        onClose={confirmBoxClose}
      />
    </CRow>
  );
};
